'use client';

import { motion, Transition, useReducedMotion } from 'motion/react';
import { useMemo } from 'react';

import TextSplitter from '~/components/molecules/TextSplitter';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

export const transition: Transition = {
  duration: 0.4,
  type: 'tween',
  ease: 'easeOut',
};

export const transitionDelay: number = 0.015;

export const defaultAsType = 'div' as const;
export type DefaultAsType = typeof defaultAsType;

export type OwnProps<E extends React.ElementType> = {
  as?: E;
  text: string | { text: string; className?: string }[];
};

export type BouncyTextProps<E extends React.ElementType> = OwnProps<E> &
  Omit<React.ComponentProps<E>, keyof OwnProps<E>>;

const containerVariants = {
  default: {},
  active: {},
} as const;

const BouncyText = <E extends React.ElementType = DefaultAsType>({
  as: asComponent,
  text,
  ...rest
}: BouncyTextProps<E>) => {
  const Component = useMemo(
    () => motion.create(asComponent || defaultAsType),
    [asComponent]
  );

  const reducedMotion = useReducedMotion();
  const hasHover = useHydrationSafeResponsive('hover');

  const shouldAnimate = !reducedMotion && hasHover;

  const textLines = typeof text === 'string' ? [{ text }] : text;

  const variants = shouldAnimate
    ? {
        default: {
          y: 0,
          opacity: 1,
        },

        active: {
          y: [0, 4, -4, 0],
          opacity: [1, 0, 0, 1],
        },
      }
    : undefined;

  return (
    <Component
      variants={containerVariants}
      initial="default"
      animate="default"
      whileHover="active"
      whileFocus="active"
      {...rest}
    >
      {textLines.map(({ text, className }, index) => (
        <TextSplitter
          key={index}
          text={text}
          className={className}
          generateLetterProps={({ index }) => ({
            className: 'inline-block relative',

            variants,
            transition: shouldAnimate
              ? {
                  type: 'tween',
                  ease: ['easeOut', 'linear', 'easeOut'],
                  duration: transition.duration * 2,
                  times: [0, 0.35, 0.35, 1],
                  delay: index * transitionDelay,
                }
              : undefined,
          })}
          useMotion
        />
      ))}
    </Component>
  );
};

export default BouncyText;
